<template>
  <v-navigation-drawer
    v-if="!embedded"
    v-model="isOpen"
    location="right"
    :temporary="!pushContent"
    :scrim="false"
    :width="380"
    class="ai-drawer"
  >
    <AiAssistantChatBody
      :hide-close-button="false"
      :load-context-on-mount="false"
      :context-loader="contextLoader"
    />
  </v-navigation-drawer>

  <v-card
    v-else
    rounded="xl"
    elevation="0"
    class="ai-drawer-embedded d-flex flex-column flex-grow-1 min-h-0"
    :style="{ border: '1px solid rgba(var(--v-border-color), 0.12)' }"
  >
    <AiAssistantChatBody
      hide-close-button
      hide-header
      compact
      load-context-on-mount
      :context-loader="contextLoader"
    />
  </v-card>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'

import AiAssistantChatBody from '@/components/AiAssistantChatBody.vue'
import { useAiAssistantStore } from '@/stores/aiAssistant'

withDefaults(
  defineProps<{
    /** Push app content to the side instead of overlaying it */
    pushContent?: boolean
    /** Show a semi-transparent overlay on the app when drawer is open */
    withOverlay?: boolean
    /** Called when the drawer opens; the returned string is set as AI context */
    contextLoader?: () => string | Promise<string>
    /**
     * Inline card layout (e.g. onboarding two-column step) instead of a navigation drawer.
     */
    embedded?: boolean
  }>(),
  {
    pushContent: false,
    withOverlay: false,
    contextLoader: undefined,
    embedded: false,
  }
)

const aiStore = useAiAssistantStore()
const { isOpen } = storeToRefs(aiStore)
</script>

<style scoped>
.ai-drawer {
  border-left: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.ai-drawer :deep(.v-navigation-drawer__content) {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ai-drawer-embedded {
  min-height: 0;
  max-height: 100%;
  height: 100%;
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ai-drawer-embedded :deep(.v-card__content),
.ai-drawer-embedded :deep(.v-card-item),
.ai-drawer-embedded :deep(.v-card-text) {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
}

.ai-drawer-embedded :deep(.ai-chat-body) {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}
</style>
